<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import './MpfInputDate.scss';
  import type { MpfInputDateProps, MpfInputDateEmits } from './MpfInputDate.props';
  import { mpfInputDateDefaults } from './MpfInputDate.props';

  import MpfInputText from '../MpfInputText/MpfInputText.vue';
  import MpfHint from '../MpfHint/MpfHint.vue';

  const props = withDefaults(defineProps<MpfInputDateProps>(), mpfInputDateDefaults);
  const emit = defineEmits<MpfInputDateEmits>();

  // internal parts
  const day = ref('');
  const month = ref('');
  const year = ref('');

  const composedDate = ref(''); // e.g. "1984-03-15" or ""

  // ids
  const dateInputId = 'mpf-date-input';
  const hintId = 'mpf-date-hint';
  const errorId = 'mpf-date-error';

  const hasErrors = computed(() => (props.errors?.length ?? 0) > 0);

  const inputGroupClasses = computed(() => [hasErrors.value ? 'mpf-form-group--error' : '']);

  const legendHeadingTag = computed(() =>
    props.legendHeadingLevel
      ? (`h${props.legendHeadingLevel}` as keyof HTMLElementTagNameMap)
      : 'div',
  );
  const legendHeadingSize = computed(() =>
    props.legendHeadingSize ? `mpf-fieldset__legend--${props.legendHeadingSize}` : null,
  );

  const legendWeight = computed(() =>
    props.legendFontWeight ? `mpf-fieldset__font-weight--${props.legendFontWeight}` : null,
  );

  const legendLabel = computed(() => props.label || 'What is your date of birth?');
  const hintText = computed(() => props.hint || 'For example, 15 3 1984');

  function onBlurDay(event: Event) {
    if (day.value.length === 1) {
      day.value = day.value.padStart(2, '0');
    }
    onBlurGeneric(event);
  }

  function onBlurMonth(event: Event) {
    if (month.value.length === 1) {
      month.value = month.value.padStart(2, '0');
    }
    onBlurGeneric(event);
  }

  function onBlurYear(event: Event) {
    // No padding for year
    onBlurGeneric(event);
  }

  function onBlurGeneric(event: Event) {
    updateModelFromParts(event);
    emit('blur', event);
  }

  watch(day, (val) => {
    if (val.length > 2) day.value = val.slice(0, 2);
  });

  watch(month, (val) => {
    if (val.length > 2) month.value = val.slice(0, 2);
  });

  watch(year, (val) => {
    if (val.length > 4) year.value = val.slice(0, 4);
  });

  watch(
    () => props.modelValue,
    (val) => {
      if (!val) {
        day.value = '';
        month.value = '';
        year.value = '';
        composedDate.value = '';
        return;
      }

      const parts = String(val).split('-');
      if (parts.length === 3) {
        const [y, m, d] = parts;
        year.value = y ?? '';
        month.value = m ?? '';
        day.value = d ?? '';
        composedDate.value = String(val); // keep ref in sync
      } else {
        composedDate.value = String(val);
      }
    },
    { immediate: true },
  );

  // build YYYY-MM-DD from parts and emit
  function updateModelFromParts(sourceEvent?: Event) {
    const d = day.value;
    const m = month.value;
    const y = year.value;

    // all empty → clear
    if (!d && !m && !y) {
      composedDate.value = '';
      emit('update:modelValue', '');
      if (sourceEvent) emit('input', sourceEvent);
      return;
    }

    // incomplete → don't emit model updates
    if (d.length < 1 || m.length < 1 || y.length < 1) {
      if (sourceEvent) emit('input', sourceEvent);
      return;
    }

    // ENG-21799 Pad here, not just on blur: the endpoint round-trips the string and
    // rejects an unpadded `1984-3-15`, and blur does not always fire before submit.
    const dd = d.padStart(2, '0');
    const mm = m.padStart(2, '0');

    const iso = `${y}-${mm}-${dd}`;
    composedDate.value = iso;

    emit('update:modelValue', iso);
    if (sourceEvent) emit('input', sourceEvent);
  }

  // emits
  function onInput(e: Event) {
    updateModelFromParts(e);
  }

  function onChange(e: Event) {
    updateModelFromParts(e);
    emit('change', e);
  }

  function onFocus(e: Event) {
    emit('focus', e);
  }
</script>

<template>
  <div class="mpf-form-group" :class="inputGroupClasses">
    <fieldset
      class="mpf-fieldset"
      role="group"
      :aria-describedby="[hintId, hasErrors ? errorId : null].filter(Boolean).join(' ')"
    >
      <legend class="mpf-fieldset__legend" :class="[legendHeadingSize, legendWeight]">
        <component
          :is="legendHeadingTag || 'div'"
          class="mpf-fieldset__heading"
          :class="legendWeight"
        >
          {{ legendLabel }}
        </component>
      </legend>

      <MpfHint :id="hintId" :hint="hintText" :sr-hint="srHint" />

      <div v-if="hasErrors" :id="errorId" class="mpf-error-message" role="alert" aria-live="polite">
        {{ errors![0] }}
      </div>

      <div :id="dateInputId" class="mpf-date-input">
        <div class="mpf-date-input__item">
          <MpfInputText
            :id="`${dateInputId}-day`"
            name="day"
            label="Day"
            :width="2"
            maxlength="2"
            v-model:modelValue="day"
            @input="onInput"
            @change="onChange"
            @blur="onBlurDay"
            @focus="onFocus"
            :numbers-only="true"
            :required="true"
            :class="!day && hasErrors ? 'mpf-input--error' : null"
          />
        </div>
        <div class="mpf-date-input__item">
          <MpfInputText
            :id="`${dateInputId}-month`"
            name="month"
            label="Month"
            :width="2"
            maxlength="2"
            v-model:modelValue="month"
            @input="onInput"
            @change="onChange"
            @blur="onBlurMonth"
            @focus="onFocus"
            :numbers-only="true"
            :required="true"
            :class="!month && hasErrors ? 'mpf-input--error' : null"
          />
        </div>
        <div class="mpf-date-input__item">
          <MpfInputText
            :id="`${dateInputId}-year`"
            name="year"
            label="Year"
            :width="4"
            maxlength="4"
            v-model:modelValue="year"
            @input="onInput"
            @change="onChange"
            @blur="onBlurYear"
            @focus="onFocus"
            :numbers-only="true"
            :required="true"
            :class="!year && hasErrors ? 'mpf-input--error' : null"
          />
        </div>
      </div>
    </fieldset>
  </div>
</template>
